import { useEffect, useMemo, useState } from "react"
import { PageProps } from "../../components/types"

import { BsCalendarDate, BsCheck2, BsClipboardCheck } from "react-icons/bs"
import DataGrid, { RowsChangeData } from 'react-data-grid'
import { BulkInspectionLogColumn } from '../../constants/Column'
import _, { groupBy as rowGrouper } from 'lodash';

import {
  Tab 
} from '@headlessui/react'
import {
  Card
} from '@aws-amplify/ui-react'
import DatePicker from 'react-datepicker'
import { SearchField } from '@aws-amplify/ui-react';
import { DisplayMode } from "../../constants/EnumType"
import useBulkInpsectionLog from "../../hooks/useBulkInspectionLog"
import { BulkStatus, DgtPdOrderOperationLog } from "../../models"
import { qcApproveBulkInspectionLogs } from "../../states/PicklistAction"
import { FiPackage } from "react-icons/fi"
import { HiQueueList } from "react-icons/hi2"
import { formatDateFromUnix } from "../../utilities/Format"
import { AiOutlineClose } from "react-icons/ai"
import EmptyRowsRenderer from "../../components/EmptyRow"

const BulkInspectionApprove = ({ title, division, line, from, to, username }: PageProps) => {

  const [ keyword, setKeyword ] = useState<string>('')
  const [ displayMode ] = useState<DisplayMode>(DisplayMode.TABLE)
  const [ startDate, setStartDate ] = useState<Date>(from || new Date());
  const [ endDate, setEndDate ] = useState<Date>(to || new Date());
  const [ filterIdx, setFilterIdx ] = useState<number>(0)

  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<unknown>>(
    () => new Set<unknown>([''])
  )

  const [ operationLogs, mutate ] = useBulkInpsectionLog(startDate, endDate, keyword)

  const onChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const onRowsChange = async (rows: DgtPdOrderOperationLog[], 
    changedInfo: RowsChangeData<DgtPdOrderOperationLog>) => {
    const changedRowIdx = changedInfo.indexes[0]
    if (changedRowIdx < 0) return

    const changedRow = rows[changedRowIdx]

    try {
      if (changedRow.approveBulkStatusDateTime! > 0) {
        const updated = await qcApproveBulkInspectionLogs(changedRow, username!)
        if (updated) mutate(updated)
      }
    } catch (err) {
      alert('เกิดความผิดพลาด กรุณาตรวจสอบ Internet และลองอีกครั้ง')
    }
  }

  const filterLabels = useMemo(() => {
    const allCount = operationLogs.filter(rt => rt.bulkStatus === BulkStatus.PASS || rt.bulkStatus === BulkStatus.REJECT).length
    const approvedCount = operationLogs.filter(rt => rt.approveBulkStatusDateTime! > 0).length

    return [
      { label: 'รอการตรวจสอบ', count: allCount - approvedCount },
      { label: 'ตรวจสอบแล้ว', count: approvedCount },
      { label: 'ทั้งหมด', count: allCount }
    ]
  }, [ operationLogs ])

  const filteredBulkOperationLogs = useMemo((): readonly DgtPdOrderOperationLog[] => {

    let operationLogsGroupBy = _.groupBy(operationLogs, 'productionOrderId')
    let oLGB = Object.values(operationLogsGroupBy)
    let oLGBWaitApprove:DgtPdOrderOperationLog[][] = []
    let oLGBApproved:DgtPdOrderOperationLog[][] = oLGB.map( po => {
      if( po.filter( p => p.approveBulkStatusDateTime! > 0).length > 0 ) return po
      else {
        oLGBWaitApprove.push(po)
        return []
      }
    })
    const operationLogsIsApproved:DgtPdOrderOperationLog[] = oLGBApproved.flat()
    const operationLogsWaitApprove:DgtPdOrderOperationLog[] = oLGBWaitApprove.flat()

    if (filterIdx === 0) {
      return operationLogsWaitApprove
    } else if (filterIdx === 1) {
      return operationLogsIsApproved
    } else {
      return operationLogs
    }
  }, [ operationLogs, filterIdx ]);


  useEffect(() => {
    if (filteredBulkOperationLogs.length > 0) {
      setExpandedGroupIds(new Set(filteredBulkOperationLogs.map(itm => itm.productionOrderId)))
    }
  }, [ filteredBulkOperationLogs ])

  return <div className="page-container">
    {/* Header v2 */}
    <div className='space-y-3 '>
      <h3>{title}</h3>
      <div className='flex flex-col justify-between gap-4 md:flex-row'>
        <div className="relative">
          <span className='absolute inset-y-0 left-0 z-10 flex items-center px-3 text-muted'>
            <BsCalendarDate />
          </span>
          <DatePicker
            className='mr-5 pl-9 input-form-date'
            selected={startDate}
            onChange={onChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            dateFormat="dd/MM/yyyy"
          />
        </div>
        <div>
          <SearchField label="search" placeholder='ค้นหาด้วย PD Order...' onChange={v => setKeyword(v.currentTarget.value)}/>
        </div>
      </div>
    </div>
    {/* Tab Menu */}
    <Tab.Group
      onChange={setFilterIdx}
      >
      <Tab.List className='flex font-light text-center text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700'>
        {filterLabels.map(filter => 
          <Tab
            key={filter.label}
            className={({ selected }) =>
              `${selected ? 'ttext-[#007bff] border-b-2 border-[#007bff] font-bold':'text-gray-400'} p-3`
            }
          >{filter.label}<span className='p-1 ml-2 text-xs font-bold text-white rounded-full bg-[#007bff]'>{filter.count}</span></Tab>
        )}
      </Tab.List>
    </Tab.Group>
    { displayMode === DisplayMode.TABLE ? 
    <DataGrid className="mt-4" style={{ height: "100%", contain: "none" }}
      columns={BulkInspectionLogColumn}
      rows={filteredBulkOperationLogs}
      onRowsChange={onRowsChange}
      rowHeight={(arg) => {
        if (arg.type === "GROUP") return 40
        else return 50
      }}
      components={{ noRowsFallback: <EmptyRowsRenderer message={"ไม่มีรายการ"}/> }}
      rowKeyGetter={(row) => row.id}
      groupBy={['productionOrderId']}
      rowGrouper={rowGrouper}
      expandedGroupIds={expandedGroupIds}
      onExpandedGroupIdsChange={setExpandedGroupIds}

    /> : <div className='grid grid-cols-1 gap-3 mt-4 md:grid-cols-2 xl:grid-cols-3'>
      {filteredBulkOperationLogs.map(ret => <Card key={ret.id} variation="elevated" borderRadius="medium">
        <div className='w-full space-y-3'>
          <div className='flex items-center justify-between '>
            <h5 className='flex items-center gap-2 font-bold'><BsClipboardCheck />{ret.productionOrderId}</h5>
            {/* TODO - Show Badge when approved - approvedWasteReturnDateTime > 0 */}
            {/* <Badge variation="success"><BsCheck2 />APPROVED</Badge> */}
          </div>
          <h5 className='flex items-center gap-2 font-bold'><FiPackage />{ret.operationNo} - {ret.pdOrderOperationId}</h5>
          <div className='flex justify-between'>
            <h6 className='flex items-center gap-3 font-light'><HiQueueList />LOT. {ret.batchNo || ''}</h6>
            <h4 className='font-bold'>{ret.bulkUsedQty}</h4>
          </div>
          <div className='flex items-center justify-between '>
            <p className='text-sm italic font-light'>บันทึกเมื่อ : {formatDateFromUnix(ret.bulkStatusDateTime)}</p>
            {/* TODO - Show time */}
            {/* <p className='text-sm italic font-light'>อนุมัติเมื่อ : {formatDateFromUnix(ret.approvedWasteReturnDateTime)}</p> */}
          </div>
          <div className='flex w-full gap-2'>
            <button className='flex-1 gap-1 btn-outline-danger'><AiOutlineClose size={20}/>ปฎิเสธ</button>
            <button className='flex-1 gap-1 btn-success'><BsCheck2 size={20}/>อนุมัติ</button>
          </div>
        </div>
      </Card>)}
    </div>
    }
  </div>
}

export default BulkInspectionApprove